import { XStack } from 'tamagui';

import { type ColorTokenKey } from '../../themes/SmoothieColorTokens';
import { hitSlop } from '../componentHelpers';
import { Icon, type IconType } from '../icons/Icon';
import { LabelRegular } from '../text/Typography';

export type BadgeLabelVariant =
    | 'neutral'
    // for use on a gray ($gray200) surface, where 'neutral' would blend in
    | 'neutral-alt'
    | 'positive'
    | 'warning'
    | 'danger'
    // non-semantic colors, e.g. for data viz
    | 'green'
    | 'blue'
    | 'marigold'
    | 'gray';

export type BadgeActionVariant = 'neutral' | 'primary' | 'warning' | 'danger';

const LabelTextColor: Record<BadgeLabelVariant, ColorTokenKey> = {
    neutral: '$gray1100',
    'neutral-alt': '$gray1100',
    positive: '$green1100',
    warning: '$orange1100',
    danger: '$red1100',
    green: '$green1100',
    blue: '$dropletBlue1100',
    marigold: '$marigold1100',
    gray: '$gray1100',
};

const LabelBackgroundColor: Record<BadgeLabelVariant, ColorTokenKey> = {
    neutral: '$gray200',
    'neutral-alt': '$gray400',
    positive: '$green300',
    warning: '$orange300',
    danger: '$red300',
    green: '$green300',
    blue: '$dropletBlue300',
    marigold: '$marigold300',
    gray: '$gray300',
};

const ActionTextColor: Record<BadgeActionVariant, ColorTokenKey> = {
    neutral: '$gray1100',
    primary: '$white',
    warning: '$orange1100',
    danger: '$red1100',
};

const ActionBackgroundColor: Record<BadgeActionVariant, ColorTokenKey> = {
    neutral: '$gray200',
    primary: '$black',
    warning: '$orange300',
    danger: '$red200',
};

const disabledTextColor: ColorTokenKey = '$gray800';
const disabledBackgroundColor: ColorTokenKey = '$gray200';

export interface BadgeLabelProps {
    variant: BadgeLabelVariant;
    text: string;
    icon?: IconType;
    size?: 'regular' | 'large';
}

interface BadgeActionProps {
    variant: BadgeActionVariant;
    text: string;
    icon?: IconType;
    onPress?: () => void;
    disabled?: boolean;
}

export const BadgeLabel = ({
    variant,
    text,
    icon,
    size = 'large',
}: BadgeLabelProps) => {
    const paddingHorizontal = size === 'large' ? 14 : 8;
    const paddingVertical = size === 'large' ? 8 : 4;

    return (
        <XStack
            paddingHorizontal={paddingHorizontal}
            paddingVertical={paddingVertical}
            gap={4}
            borderRadius={999}
            backgroundColor={LabelBackgroundColor[variant]}
            alignItems="center"
            width="fit-content"
        >
            {icon && (
                <Icon icon={icon} size={12} color={LabelTextColor[variant]} />
            )}
            <LabelRegular color={LabelTextColor[variant]}>{text}</LabelRegular>
        </XStack>
    );
};

export const BadgeAction = ({
    variant,
    text,
    icon,
    onPress,
    disabled,
}: BadgeActionProps) => {
    const textColor = disabled ? disabledTextColor : ActionTextColor[variant];
    const backgroundColor = disabled
        ? disabledBackgroundColor
        : ActionBackgroundColor[variant];
    return (
        <XStack
            paddingHorizontal={14}
            paddingVertical={8}
            gap={4}
            borderRadius={999}
            backgroundColor={backgroundColor}
            alignItems="center"
            width="auto"
            onPress={disabled ? undefined : onPress}
            cursor={disabled ? 'default' : 'pointer'}
            {...(!disabled && {
                pressStyle: {
                    scale: 0.96,
                    opacity: 0.87,
                },
            })}
            animation="button"
            animateOnly={['transform', 'opacity']}
            {...hitSlop(4)}
        >
            {icon && <Icon icon={icon} size={12} color={textColor} />}
            <LabelRegular color={textColor}>{text}</LabelRegular>
        </XStack>
    );
};
